import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { KpiCard, AdminCard, SectionHeader, StatusPill, EmptyState, formatCHF, btnGhost } from "@/lib/admin/ui";
import {
  Users, DollarSign, TrendingUp, Briefcase, CheckCircle2, Ticket,
  AlertTriangle, Percent, ListChecks, CalendarClock, ArrowRight, Activity,
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { format, subMonths, startOfMonth } from "date-fns";
import { it } from "date-fns/locale";

export const Route = createFileRoute("/admin/")({
  component: AdminHome,
});

function AdminHome() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-home"],
    queryFn: async () => {
      const [projects, tickets, subs, invoices, tasks, leads] = await Promise.all([
        supabase.from("projects").select("id, owner_id, kanban_status, status, delivery_date, name, progress, plan, created_at"),
        supabase.from("tickets").select("id, priority, status, created_at"),
        supabase.from("subscriptions").select("id, price, currency, status"),
        supabase.from("invoices").select("id, amount, currency, status, paid_at, issued_at"),
        supabase.from("tasks").select("id, status, due_date"),
        supabase.from("leads").select("id, status, created_at"),
      ]);
      return {
        projects: projects.data ?? [],
        tickets: tickets.data ?? [],
        subs: subs.data ?? [],
        invoices: invoices.data ?? [],
        tasks: tasks.data ?? [],
        leads: leads.data ?? [],
      };
    },
    refetchInterval: 60_000,
  });

  const totalClients = new Set((data?.projects ?? []).map((p) => p.owner_id)).size;
  const activeSubs = (data?.subs ?? []).filter((s) => s.status === "active");
  const mrr = activeSubs.reduce((s, x) => s + Number(x.price ?? 0), 0);
  const revenueMonth = (data?.invoices ?? [])
    .filter((i) => i.status === "paid" && i.paid_at && new Date(i.paid_at) > startOfMonth(new Date()))
    .reduce((s, i) => s + Number(i.amount ?? 0), 0);
  const projInProgress = (data?.projects ?? []).filter((p) => !["delivered", "live"].includes(p.kanban_status ?? "") && p.status !== "delivered").length;
  const projDelivered = (data?.projects ?? []).filter((p) => p.kanban_status === "delivered" || p.status === "delivered" || p.status === "live").length;
  const openTickets = (data?.tickets ?? []).filter((t) => !["completed", "closed"].includes(t.status)).length;
  const urgentTickets = (data?.tickets ?? []).filter((t) => t.priority === "urgent" && !["completed", "closed"].includes(t.status)).length;
  const leadsTotal = (data?.leads ?? []).length;
  const leadsWon = (data?.leads ?? []).filter((l) => l.status === "won").length;
  const conversion = leadsTotal > 0 ? Math.round((leadsWon / leadsTotal) * 100) : 0;
  const today = new Date().toDateString();
  const todayTasks = (data?.tasks ?? []).filter((t) => t.due_date && new Date(t.due_date).toDateString() === today && t.status !== "done").length;

  // Revenue chart 6 months
  const revenueSeries = Array.from({ length: 6 }).map((_, i) => {
    const d = subMonths(new Date(), 5 - i);
    const monthStart = startOfMonth(d);
    const nextMonth = startOfMonth(subMonths(d, -1));
    const total = (data?.invoices ?? [])
      .filter((inv) => inv.status === "paid" && inv.paid_at && new Date(inv.paid_at) >= monthStart && new Date(inv.paid_at) < nextMonth)
      .reduce((s, inv) => s + Number(inv.amount ?? 0), 0);
    return { month: format(d, "MMM", { locale: it }), revenue: total };
  });
  const spark = revenueSeries.map((r) => r.revenue);

  const upcomingDeliveries = (data?.projects ?? [])
    .filter((p) => p.delivery_date && new Date(p.delivery_date) > new Date())
    .sort((a, b) => new Date(a.delivery_date!).getTime() - new Date(b.delivery_date!).getTime())
    .slice(0, 6);

  return (
    <div className="space-y-8 max-w-[1600px]">
      <SectionHeader eyebrow="Admin OS" title="Dashboard" description="Il polso di FlashWeb in tempo reale."
        actions={<Link to="/admin/projects" className={btnGhost}>Vai al Kanban <ArrowRight className="h-3.5 w-3.5" /></Link>} />

      {/* KPI grid */}
      <div className="grid gap-3 grid-cols-2 md:grid-cols-3 xl:grid-cols-5">
        <KpiCard icon={Users} label="Clienti totali" value={String(totalClients)} tone="fuchsia" sub={`${projInProgress} progetti attivi`} />
        <KpiCard icon={TrendingUp} label="MRR" value={formatCHF(mrr)} tone="emerald" sub={`${activeSubs.length} abbonamenti`} spark={spark} />
        <KpiCard icon={DollarSign} label="Revenue mese" value={formatCHF(revenueMonth)} tone="cyan" spark={spark} />
        <KpiCard icon={Briefcase} label="Progetti in corso" value={String(projInProgress)} tone="violet" />
        <KpiCard icon={CheckCircle2} label="Consegnati" value={String(projDelivered)} tone="emerald" />
        <KpiCard icon={Ticket} label="Ticket aperti" value={String(openTickets)} tone={urgentTickets > 0 ? "amber" : "slate"} />
        <KpiCard icon={AlertTriangle} label="Ticket urgenti" value={String(urgentTickets)} tone={urgentTickets > 0 ? "red" : "slate"} />
        <KpiCard icon={Percent} label="Conversion" value={`${conversion}%`} tone="fuchsia" sub={`${leadsWon}/${leadsTotal} lead`} />
        <KpiCard icon={ListChecks} label="Task oggi" value={String(todayTasks)} tone="cyan" />
        <KpiCard icon={CalendarClock} label="Consegne 7g" value={String(upcomingDeliveries.filter((p) => (new Date(p.delivery_date!).getTime() - Date.now()) < 7 * 864e5).length)} tone="amber" />
      </div>

      {/* Charts + Timeline */}
      <div className="grid gap-4 lg:grid-cols-3">
        <AdminCard className="p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/40">Revenue trend</div>
              <div className="font-semibold mt-0.5">Ultimi 6 mesi</div>
            </div>
            <div className="text-2xl font-semibold tabular-nums">{formatCHF(revenueSeries.reduce((s, r) => s + r.revenue, 0))}</div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenueSeries} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="revfill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#d946ef" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#d946ef" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip contentStyle={{ background: "rgba(15,15,25,0.95)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, fontSize: 12 }} labelStyle={{ color: "rgba(255,255,255,0.6)" }} formatter={(v: number) => [formatCHF(v), "Revenue"]} />
                <Area type="monotone" dataKey="revenue" stroke="#d946ef" strokeWidth={2} fill="url(#revfill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </AdminCard>

        <AdminCard className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/40">Prossime consegne</div>
              <div className="font-semibold mt-0.5">Timeline</div>
            </div>
            <CalendarClock className="h-4 w-4 text-white/40" />
          </div>
          {upcomingDeliveries.length === 0 ? (
            <EmptyState icon={CalendarClock} title="Nessuna consegna" description="Aggiungi date di consegna dai progetti." />
          ) : (
            <div className="space-y-3">
              {upcomingDeliveries.map((p) => {
                const days = Math.ceil((new Date(p.delivery_date!).getTime() - Date.now()) / 864e5);
                return (
                  <Link key={p.id} to="/admin/projects" className="block p-3 rounded-xl bg-white/[0.02] hover:bg-white/[0.04] border border-white/[0.06]">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium text-sm truncate">{p.name}</div>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${days <= 3 ? "bg-red-500/20 text-red-300" : days <= 7 ? "bg-amber-500/20 text-amber-300" : "bg-white/5 text-white/60"}`}>{days}g</span>
                    </div>
                    <div className="mt-2 h-1 rounded-full bg-white/5 overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-fuchsia-500 to-cyan-400" style={{ width: `${p.progress ?? 0}%` }} />
                    </div>
                    <div className="mt-1 text-[10px] text-white/40 flex justify-between">
                      <span>{format(new Date(p.delivery_date!), "d MMM yyyy", { locale: it })}</span>
                      <span>{p.progress ?? 0}%</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </AdminCard>
      </div>

      {/* Activity feed */}
      <RecentActivity />
    </div>
  );
}

function RecentActivity() {
  const { data } = useQuery({
    queryKey: ["admin-activity"],
    queryFn: async () => (await supabase.from("activity_log").select("*").order("created_at", { ascending: false }).limit(15)).data ?? [],
  });
  return (
    <AdminCard className="p-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/40">Attività recente</div>
          <div className="font-semibold mt-0.5 flex items-center gap-2"><Activity className="h-4 w-4 text-fuchsia-300" /> Log operazioni</div>
        </div>
      </div>
      {(data ?? []).length === 0 ? (
        <div className="py-8 text-center text-sm text-white/40">Nessuna attività registrata ancora</div>
      ) : (
        <div className="divide-y divide-white/[0.05]">
          {(data ?? []).map((a) => (
            <div key={a.id} className="py-2.5 flex items-center gap-3 text-sm">
              <StatusPill status={a.action} />
              <span className="text-white/70">{a.entity_type}</span>
              <span className="ml-auto text-[11px] text-white/40">{format(new Date(a.created_at), "d MMM HH:mm", { locale: it })}</span>
            </div>
          ))}
        </div>
      )}
    </AdminCard>
  );
}
